export const ROLES = {
  systemAdmin: "System Admin",
  systemViewer: "System Viewer",
  orgAdmin: "Org Admin",
  orgUser: "Org User",
};

export const REVERSE_LOOKUP_ROLES = {
  [ROLES.systemAdmin]: "systemAdmin",
  [ROLES.systemViewer]: "systemViewer",
  [ROLES.orgAdmin]: "orgAdmin",
  [ROLES.orgUser]: "orgUser",
};

// Roles that can only be assigned on the Populi org.
export const SYSTEM_ROLE_KEYS = ["systemAdmin", "systemViewer"];

export const ANALYTICS_HUB_USER_ROLES = {
  pro: "Pro",
  standard: "Standard",
  restricted: "Restricted",
  none: "None",
};

export const REVERSE_LOOKUP_AHU_ROLES = {
  [ANALYTICS_HUB_USER_ROLES.pro]: "pro",
  [ANALYTICS_HUB_USER_ROLES.standard]: "standard",
  [ANALYTICS_HUB_USER_ROLES.restricted]: "restricted",
  [ANALYTICS_HUB_USER_ROLES.none]: "none",
};

export const TABLEAU_ROLES = {
  viewer: "Viewer",
  explorer: "Explorer",
  creator: "Creator",
  none: "None",
};

export const REVERSE_LOOKUP_TABLEAU_ROLES = {
  [TABLEAU_ROLES.viewer]: "viewer",
  [TABLEAU_ROLES.explorer]: "explorer",
  [TABLEAU_ROLES.creator]: "creator",
};

export const API_ERRORS = {
  PASSWORD_EXPIRED: "auth/password-expired",
  PASSWORD_EXPIRED_WITHIN_GRACE_PERIOD:
    "auth/password-expired-within-grace-period",
};

export const DEFAULT_USER = {
  active: true,
  status: "Active",
  userId: "",
  email: "",
  alternateEmail: "",
  name: "",
  organizationId: "",
  role: "",
  username: "",
  tableauConfig: { license: "none", tableauUser: false },
  direct_data_access_enabled: false,
  memberships: [],
};

export const DEFAULT_ORG = {
  active: false,
  name: "",
  id: "",
  domain: [],
  callbackUrls: [],
  primaryContact: { name: "", email: "" },
  licenses: {
    tableau_user_licenses: {
      explorer_count: 0,
      viewer_count: 0,
      // Can't add creator_count here because api throws error when org !== populi
    },
    user_limit: 20,
    analytics_hub_user_licenses: {
      pro_count: 0,
    },
  },
  preferences: {
    bi_portal: {
      default_states: [],
    },
  },
  customer_segment: "",
  medicare_licensed: false,
};

export const IMMUTABLE_LICENSES_PROPERTIES = ["deployment", "base_attributes"];

export const DIRECT_DATA_ACCESS_STATUS_LABEL = {
  ENABLED: "Enabled",
  DISABLED: "Disabled",
};

export const RELEASE_STAGES = {
  ALPHA: "alpha", // not used yet
  BETA: "beta",
  GA: "general-availability",
};

export const FLAG_KEYS = {
  MANAGED_PROVIDERS: "feature:managed-providers",
  ANALYTICS_HUB: "feature:analytics-hub",
};

export const FLAG_ENTITIES = {
  ORGANIZATION: "organization",
  USER: "user", // not used yet
};

// Populi's own organization. This org is treated specially by feature flagging:
// it always has BETA + GA stage access, and is the only org allowed to view an
// empty Analytics Hub (see isAnalyticsHubEnabled).
export const POPULI_ORG_ID = "populi";

export const PROVIDER_LICENSE_CONFIGS = {
  ALIAS: "provider_alias",
  RELATIONSHIP: "provider_relationship",
};

export const PROVIDER_LICENSE_PERMISSIONS = "configurations:read";

export const catalogQueryKey = [
  "catalog",
  {
    include: [
      "bi",
      "models",
      "data",
      "fields",
      "field_values",
      "licensable_field_groups",
    ],
    filters: [
      {
        catalog: "products",
        field: "is_license_required",
        operator: "=",
        value: true,
      },
    ],
  },
];

export const prCatalogQueryKey = [
  "pr-catalog",
  {
    isPrCatalog: true,
    include: [
      "bi",
      "models",
      "data",
      "fields",
      "field_values",
      "licensable_field_groups",
    ],
    filters: [
      {
        catalog: "products",
        field: "is_license_required",
        operator: "=",
        value: true,
      },
    ],
  },
];

export const PERMISSIONS = {
  [ROLES.systemAdmin]: [
    "admin:read",
    "analytics:read",

    // Analytics Hub permissions
    "analytics-hub:read",
    "analytics-hub:saved-views:create",
    "analytics-hub:saved-views:delete-any",

    // Organization permissions
    "organizations:read",
    "organizations:create",

    // All users permissions
    "all:users:read",
    "all:users:create",

    // Single organization permissions
    "organization:read",
    "organization:update",

    // Organization user permissions
    "users:read",
    "users:create",

    // Configurations permissions
    "configurations:read",
    "configurations:write",

    // Product license permissions
    "product-license:read",
    "product-license:write",

    // Catalog permissions
    "catalog:read",
    "catalog-version:read",

    // User profile permissions
    "user:read-profile",

    "user:update-profile",
    "user:read-password-section", // TODO: Separate reset from update read permissions
    "user:read-password-reset-meta",
    "user:initiate-password-reset",
  ],
  [ROLES.systemViewer]: [
    "admin:read",
    "analytics:read",

    // Analytics Hub permissions
    "analytics-hub:read",
    "analytics-hub:saved-views:create",

    // Organization permissions
    "organizations:read",

    // Single organization permissions
    "organization:read",

    // All users permissions
    "all:users:read",

    // Organization user permissions
    "users:read",

    // Configurations permissions
    "configurations:read",

    // Product license permissions
    "product-license:read",

    // Catalog permissions
    "catalog:read",
    "catalog-version:read",

    // User profile permissions
    "user:read-profile",
    "user:read-password-section",
    "user:read-password-reset-meta",
  ],
  [ROLES.orgAdmin]: [
    "admin:read",

    // Analytics Hub permissions
    "analytics-hub:read",
    "analytics-hub:saved-views:create",
    "analytics-hub:saved-views:delete-any",

    // Single organization permissions
    {
      name: "organization:read",
      rule: ({ userMembershipIds, orgIdParam }) => {
        // Make sure the required values exist first
        if (!userMembershipIds || !orgIdParam) {
          return false;
        }

        return userMembershipIds.includes(orgIdParam);
      },
    },

    // Organization user permissions
    "users:read",
    "users:create",

    // Catalog permissions
    "catalog:read",
    "catalog-version:read",

    // Configurations permissions
    "configurations:read",
    "configurations:write",

    // User profile permissions
    "user:read-profile",
    "user:update-profile",
    "user:read-password-section", // TODO: Separate reset from update read permissions
    "user:read-password-reset-meta",
    "user:initiate-password-reset",
  ],
  [ROLES.orgUser]: [
    // Analytics Hub permissions
    "analytics-hub:read",
    "analytics-hub:saved-views:create",

    // Configurations permissions
    "configurations:read",

    // Catalog permissions
    "catalog:read",
    "catalog-version:read",

    // User profile permissions
    "user:read-profile",
    // "user:update-profile", // TODO: API Team authorize this endpoint
  ],
};

export const FEATURE_FLAG_DISABLED_MESSAGE =
  "Customer has not licensed the product that enables this feature.";

export const ERROR_TYPES = {
  APPLICATION_ERROR: "applicationError",
  AUTH_ERROR: "authError",
  ROOT_ERROR: "rootError",
  PAGE_ERROR: "pageError",
};

export const REVERSE_ERROR_TYPES = {
  [ERROR_TYPES.APPLICATION_ERROR]: "Application Error",
  [ERROR_TYPES.AUTH_ERROR]: "Auth Error",
  [ERROR_TYPES.ROOT_ERROR]: "Root Error",
  [ERROR_TYPES.PAGE_ERROR]: "Page Error",
};
